import React from "react";
import { Table, Tag, Button, Tooltip, Empty, Modal } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { tablePagination } from "../../../components";

interface ChequeTableProps {
  data: any[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  onPageChange: (p: number) => void;
  onEdit: (record: any) => void;
  onUpdateStatus: (record: any) => void;
  onDelete: (id: string) => void;
}

const statusTag = (code: string, name: string) => {
  const map: Record<string, string> = {
    pending: "warning",
    cleared: "success",
    bounced: "error",
    cancelled: "default",
  };
  return <Tag color={map[code] || "default"}>{name}</Tag>;
};

export const ChequeTable: React.FC<ChequeTableProps> = ({
  data,
  total,
  page,
  pageSize,
  loading,
  onPageChange,
  onEdit,
  onUpdateStatus,
  onDelete,
}) => {
  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 90,
      render: (v: string) => (
        <Tag color={v === "in" ? "green" : "purple"}>
          {v === "in" ? "Inward" : "Outward"}
        </Tag>
      ),
    },
    {
      title: "Cheque No",
      dataIndex: "cheque_no",
      key: "cheque_no",
      render: (v: string, record: any) => (
        <div>
          <div className="text-xs font-semibold text-gray-800">{v}</div>
          <div className="text-xs text-gray-400">
            {record.bank_name}
            {record.bank_branch ? ` · ${record.bank_branch}` : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "cheque_date",
      key: "cheque_date",
      render: (v: string) => (
        <span className="text-xs text-gray-600">
          {dayjs(v).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Payee / Account",
      dataIndex: "payee",
      key: "payee",
      render: (v: string, record: any) => (
        <div>
          <div className="text-xs text-gray-700">{v || "—"}</div>
          {record.bank_account_name && (
            <div className="text-xs text-gray-400">
              {record.bank_account_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status_name",
      key: "status_name",
      render: (v: string, record: any) => statusTag(record.status_code, v),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-semibold text-gray-800">
          LKR{" "}
          {Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      width: 120,
      render: (_: any, record: any) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip title="Update Status">
            <Button
              type="text"
              size="small"
              icon={<ClockCircleOutlined />}
              onClick={() => onUpdateStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() =>
                Modal.confirm({
                  title: "Delete cheque?",
                  content: "This cannot be undone.",
                  okText: "Delete",
                  okType: "danger",
                  cancelText: "Cancel",
                  onOk: () => onDelete(record.id),
                })
              }
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded overflow-hidden">
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={tablePagination({
          current: page,
          pageSize,
          total,
          onChange: onPageChange,
          itemName: "cheques",
        })}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No cheques found."
            />
          ),
        }}
      />
    </div>
  );
};
